import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, SaveFormModule,
} from '@domain-group/suit-theme'

import ApiKeys from '@components/ApiKeys'

import useAgency, { useAgencyForm } from '@hooks/useAgency'
import useAgencySelector from '@hooks/useAgencySelector'

import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import PageHeader from '@components/PageHeader'

import Accounting from '../Accounting'
import AgencyIntegrationsDisabledBanner from '../AgencyIntegrationsDisabledBanner'
import AgencyPortals from '../AgencyPortals'
import CRMSync from '../CRMSync'
import Domain from '../Domain'
import IntegrationPlatforms from '../IntegrationPlatforms'
import Portals from '../Portals'

const PageContent = () => {
  useLocation()

  const { selectedAgencyId } = useAgencySelector()

  const agencyPayload = useAgency({ id: selectedAgencyId })
  const {
    agency,
    callbacks: {
      updateAgency: updateFn,
      selectIntegrationPlatform,
    },
    updating,
  } = agencyPayload

  const disabledIntegrationModules = agency?.options?.disabled_integration_modules || []

  const agencyFormPayload = useAgencyForm(agency)
  const { entityState, saveEnabled } = agencyFormPayload

  const updateAgencyPayload: HandleCallbackActionParams = {
    callbacks: {
      action: updateFn,
    },
    entityParams: entityState,
    toastText: 'Integrations Updated',
  }

  return (
    <>
      <Helmet>
        <title>Integrations | Realhub Admin</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button
            // @ts-expect-error
            disabled={disabledIntegrationModules?.includes('third_party_integration')}
            buttonStyle="primaryCreate"
            onClick={() => selectIntegrationPlatform()}
            size="medium"
          >
            Create
          </Button>
        )}
        activeTabBarItemKey="integrations"
        nestedNavigation
        tabBarItems={[
          {
            href: `#/agencies/${selectedAgencyId}/manage/integrations?`,
            icon: faCheckCircle,
            key: 'integrations',
            title: 'Integrations',
          },
        ]}
        title="Integrations"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.agencyManager.integrations.banner" />

            {!!disabledIntegrationModules?.length && <AgencyIntegrationsDisabledBanner />}

            <Accounting
              agencyFormPayload={agencyFormPayload}
              // @ts-expect-error
              disabled={disabledIntegrationModules.includes('accounting')}
            />

            <ApiKeys
              // @ts-expect-error
              disabled={disabledIntegrationModules.includes('api_keys')}
              filters={{ owner_id: selectedAgencyId, owner_type: 'Agency' }}
              hideFilters
              userSearchRequestOptions={{ agency_id: selectedAgencyId }}
            />

            <CRMSync
              agencyPayload={agencyPayload}
              // @ts-expect-error
              disabled={disabledIntegrationModules.includes('crm_sync')}
            />

            <AgencyPortals
              // @ts-expect-error
              disabled={disabledIntegrationModules.includes('portals')}
              // @ts-expect-error TS2322
              selectedAgencyId={selectedAgencyId}
            />

            <Portals
              // @ts-expect-error TS2322
              disabled={disabledIntegrationModules.includes('portal_options')}
              // @ts-expect-error TS2322
              selectedAgencyId={selectedAgencyId}
            />

            {/*
             // @ts-expect-error TS2322 */}
            <Domain selectedAgencyId={selectedAgencyId} />

            {/*
             // @ts-expect-error TS2322 */}
            <IntegrationPlatforms agency={agency} />

            <SaveFormModule>
              <Button
                buttonStyle="primaryCreate"
                disabled={!saveEnabled}
                loading={updating}
                marginLeft="auto"
                onClick={() => handleCallbackAction(updateAgencyPayload)}
                size="medium"
              >
                Save Details
              </Button>
            </SaveFormModule>
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.agencyManager.integrations.sidebar" />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
